/* Dr.Primal — the capture popup's shop adapter: Shopify.

   Registers as `shop` with `dp-capture.js`. Two jobs, both thin:

   - `consentReady()` delegates to `window.dpConsent.ready()` (`assets/dp-consent.js`),
     the theme's one consent reader. It resolves only after PostHog has been told the
     visitor's answer, so the popup's first event can never beat PostHog's consent state.
     If `dp-consent.js` is missing from the page it REJECTS: the engine then never opens,
     because it cannot know whether a cookie banner is waiting underneath.
   - `applyDiscount(code)` stores the code on the cart through the Cart Ajax API
     (`/cart/update.js` with `discount`), so the visitor does not have to type it.
     `discount` REPLACES the cart's codes (Shopify clears them all with `''`), so the
     cart is read first: a code already on it is left alone and reported as applied,
     and otherwise the new code is posted together with the ones already there. The
     post is `keepalive`, so a quick click through to the product page cannot cut it
     off. It
     resolves `{ ok, applicable? }`: `ok` is true when the cart came back carrying the
     code; `applicable` is Shopify's own verdict on the basket as it stands, which is
     false for an empty or non-qualifying basket and becomes true once the qualifying
     line is added. It never rejects — a failure is `{ ok: false }`, and the reward step
     falls back to telling the visitor to enter the code at checkout.

   It does not refresh the cart drawer: a code changes no line and no count, and the
   drawer re-reads the cart from the server every time it opens. */
(function () {
  'use strict';

  var CART_PATH = 'cart.js';                    // Cart Ajax read, for the codes already on the cart; relative to Shopify.routes.root
  var CART_UPDATE_PATH = 'cart/update.js';      // Cart Ajax endpoint that accepts `discount`, relative to Shopify.routes.root
  var CODE_SEPARATOR = ',';                     // how Shopify's `discount` field separates several codes
  var REQUEST_TIMEOUT_MS = 10000;               // a cart update still pending after this is abandoned and reported as not applied

  var capture = window.dpCapture;
  if (!capture) {
    console.error('[dp-capture-shopify] dp-capture.js must load before this file');
    return;
  }

  function root() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }
    return '/';
  }

  function consentReady() {
    return window.dpConsent && typeof window.dpConsent.ready === 'function'
      ? window.dpConsent.ready()
      : Promise.reject(new Error('dp-consent.js is not on this page'));
  }

  function carriesCode(cart, code) {
    var codes = (cart && cart.discount_codes) || [];
    for (var i = 0; i < codes.length; i += 1) {
      if (String(codes[i].code).toUpperCase() === String(code).toUpperCase()) return codes[i];
    }
    return null;
  }

  function readJson(response, what) {
    if (!response) throw new Error(what + ' gave no answer (offline, blocked or timed out)');
    if (!response.ok) throw new Error(what + ' responded ' + response.status);
    return response.json();
  }

  function applied(entry) {
    return { ok: true, applicable: entry.applicable === true };
  }

  function applyDiscount(code) {
    return capture
      .fetchWithin(root() + CART_PATH, { headers: { Accept: 'application/json' } }, REQUEST_TIMEOUT_MS)
      .then(function (response) {
        return readJson(response, CART_PATH);
      })
      .then(function (cart) {
        var present = carriesCode(cart, code);
        if (present) return applied(present);

        var codes = ((cart && cart.discount_codes) || []).map(function (entry) {
          return entry.code;
        });
        codes.push(code);
        return capture
          .fetchWithin(
            root() + CART_UPDATE_PATH,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ discount: codes.join(CODE_SEPARATOR) }),
              keepalive: true,
            },
            REQUEST_TIMEOUT_MS
          )
          .then(function (response) {
            return readJson(response, CART_UPDATE_PATH);
          })
          .then(function (updated) {
            var entry = carriesCode(updated, code);
            if (!entry) throw new Error('the cart came back without ' + code);
            return applied(entry);
          });
      })
      .catch(function (error) {
        console.error('[dp-capture-shopify]', error);
        return { ok: false };
      });
  }

  capture.registerAdapter('shop', {
    consentReady: consentReady,
    applyDiscount: applyDiscount,
  });
})();
